// ---------------------------------------------------------------------------
// Login view — dumb, full-screen email/password form. Owns DOM, validation UX,
// accessibility, and submit/busy/error states. Knows nothing about storage,
// credentials, routing, or session: it calls back through `onSubmit` and lets
// the bootstrap gate own the transition to the shell.
// ---------------------------------------------------------------------------

import { createElement } from '../../core/dom/helpers.js';

/**
 * @typedef {import('../../contracts/auth.js').LoginResult} LoginResult
 */

/**
 * @typedef {Object} LoginViewOptions
 * @property {(email: string, password: string) => Promise<LoginResult>} onSubmit
 * @property {{ emit: (name: string, data?: unknown) => void }} [eventBus] - For the infra-error toast.
 * @property {string} [appTitle] - Heading text. Default: 'Sign in'.
 * @property {string} [logoUrl] - When set, renders a logo above the title.
 * @property {string} [logoAlt] - Accessible name for the logo (e.g. the company name).
 *   When omitted the logo is treated as decorative (`alt=""`); provide it when the
 *   wordmark carries brand identity the heading does not.
 */

// Basic presence/shape check — the server is the real authority; this only
// stops obviously-empty submits from hitting `onSubmit`.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INVALID_CREDENTIALS_MSG = 'Invalid email or password.';
const GENERIC_ERROR_MSG = 'Something went wrong. Please try again.';
const SUBMIT_LABEL = 'Sign in';
const SUBMIT_BUSY_LABEL = 'Signing in…';

/**
 * Mount the login view.
 *
 * @param {HTMLElement} container
 * @param {LoginViewOptions} options
 * @returns {{ destroy: () => void }}
 */
export function createLoginView(container, options) {
  const { onSubmit, eventBus, appTitle = 'Sign in', logoUrl, logoAlt } = options;

  // Per-instance (NOT module-scoped): two mounts must never share this, and a
  // submit settling after destroy() must not touch detached DOM.
  let destroyed = false;

  const root = createElement('div', { class: 'login' });
  const card = createElement('div', { class: 'login-card' });
  root.appendChild(card);

  if (logoUrl) {
    // `alt` = logoAlt when the wordmark carries brand identity the heading lacks;
    // otherwise empty (decorative). Hide on load error so a missing asset never
    // shows the broken-image icon (the title below is the always-present brand).
    const logo = /** @type {HTMLImageElement} */ (
      createElement('img', { class: 'login-logo', src: logoUrl, alt: logoAlt ?? '' })
    );
    logo.addEventListener('error', () => {
      logo.style.display = 'none';
    });
    card.appendChild(logo);
  }

  const heading = createElement('h1', { class: 'login-title' }, [appTitle]);
  card.appendChild(heading);

  const form = createElement('form', { class: 'login-form', novalidate: 'novalidate' });
  card.appendChild(form);

  // Error region — assertive alert so a failed submit is announced.
  const errorRegion = createElement('div', {
    class: 'login-error',
    role: 'alert',
    hidden: 'hidden',
  });
  form.appendChild(errorRegion);

  const emailInput = /** @type {HTMLInputElement} */ (
    createElement('input', {
      type: 'email',
      id: 'login-email',
      name: 'email',
      class: 'login-input',
      autocomplete: 'username',
      'aria-required': 'true',
    })
  );
  form.appendChild(field('login-email', 'Email', emailInput));

  const passwordInput = /** @type {HTMLInputElement} */ (
    createElement('input', {
      type: 'password',
      id: 'login-password',
      name: 'password',
      class: 'login-input',
      autocomplete: 'current-password',
      'aria-required': 'true',
    })
  );
  form.appendChild(field('login-password', 'Password', passwordInput));

  // Forgot-password: visible but truly inert until the reset flow exists (a
  // disabled control changes nothing and is correctly out of tab order). It is
  // NOT an <a href="#"> (that mutates the hash).
  const forgot = createElement(
    'button',
    { type: 'button', class: 'login-link', disabled: 'disabled' },
    ['Forgot password?'],
  );
  form.appendChild(forgot);

  const submitBtn = /** @type {HTMLButtonElement} */ (
    createElement('button', { type: 'submit', class: 'login-submit' }, [SUBMIT_LABEL])
  );
  form.appendChild(submitBtn);

  // --- helpers -------------------------------------------------------------

  /**
   * Build a labelled field wrapper.
   * @param {string} id
   * @param {string} labelText
   * @param {HTMLElement} input
   */
  function field(id, labelText, input) {
    const wrap = createElement('div', { class: 'login-field' });
    wrap.appendChild(createElement('label', { for: id, class: 'login-label' }, [labelText]));
    wrap.appendChild(input);
    return wrap;
  }

  /** @param {string} message */
  function showError(message) {
    errorRegion.textContent = message;
    errorRegion.hidden = false;
  }

  function clearValidation() {
    errorRegion.textContent = '';
    errorRegion.hidden = true;
    emailInput.removeAttribute('aria-invalid');
    passwordInput.removeAttribute('aria-invalid');
  }

  /** @param {HTMLInputElement} input */
  function markInvalid(input) {
    input.setAttribute('aria-invalid', 'true');
  }

  function enterBusy() {
    form.setAttribute('aria-busy', 'true');
    submitBtn.disabled = true;
    submitBtn.classList.add('login-submit--busy');
    submitBtn.textContent = SUBMIT_BUSY_LABEL;
    emailInput.disabled = true;
    passwordInput.disabled = true;
  }

  function exitBusy() {
    form.removeAttribute('aria-busy');
    submitBtn.disabled = false;
    submitBtn.classList.remove('login-submit--busy');
    submitBtn.textContent = SUBMIT_LABEL;
    emailInput.disabled = false;
    passwordInput.disabled = false;
    emailInput.focus();
  }

  // Clear a field's invalid state as the user corrects it.
  const onEmailInput = () => emailInput.removeAttribute('aria-invalid');
  const onPasswordInput = () => passwordInput.removeAttribute('aria-invalid');
  emailInput.addEventListener('input', onEmailInput);
  passwordInput.addEventListener('input', onPasswordInput);

  /** @param {Event} event */
  async function handleSubmit(event) {
    event.preventDefault();
    if (destroyed) return;

    // Reset any prior error state before validating this attempt.
    clearValidation();

    const email = emailInput.value.trim();
    const password = passwordInput.value;

    // Mark EVERY invalid field (not just the first), then focus the first one —
    // otherwise a both-empty submit would flag only email and hide the second error.
    const emailInvalid = !EMAIL_RE.test(email);
    const passwordInvalid = password === '';
    if (emailInvalid) markInvalid(emailInput);
    if (passwordInvalid) markInvalid(passwordInput);
    if (emailInvalid || passwordInvalid) {
      showError(INVALID_CREDENTIALS_MSG);
      (emailInvalid ? emailInput : passwordInput).focus();
      return;
    }

    enterBusy();

    // Result-processing lives INSIDE the try so a malformed resolved value (e.g.
    // `null` → `result.ok` throws) is normalized to the error path, not leaked as
    // an uncaught throw that leaves the form stuck busy. `finally` guarantees the
    // busy state clears on every outcome EXCEPT a genuine success (where the gate
    // is about to destroy this view). `succeeded` gates that one exception.
    let succeeded = false;
    try {
      const result = await onSubmit(email, password);
      if (destroyed) return;

      if (result && result.ok) {
        // Success: the gate tears this view down and mounts the shell. Leave the
        // busy state as-is (the view is about to be destroyed).
        succeeded = true;
      } else if (result && result.reason === 'cancelled') {
        // Superseded by logout/teardown — not a user-visible error.
      } else if (result && result.reason === 'invalid_credentials') {
        showError(INVALID_CREDENTIALS_MSG);
      } else {
        // reason === 'error', a malformed result, or any unexpected shape.
        showError(GENERIC_ERROR_MSG);
        eventBus?.emit('toast.message.requested', { type: 'error', message: GENERIC_ERROR_MSG });
      }
    } catch {
      // Normalize a sync-throw OR rejection to the infra-error path — a bare
      // .then() would miss a function that throws before returning a promise.
      if (destroyed) return;
      showError(GENERIC_ERROR_MSG);
      eventBus?.emit('toast.message.requested', { type: 'error', message: GENERIC_ERROR_MSG });
    } finally {
      if (!destroyed && !succeeded) exitBusy();
    }
  }

  form.addEventListener('submit', handleSubmit);

  container.appendChild(root);

  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      form.removeEventListener('submit', handleSubmit);
      emailInput.removeEventListener('input', onEmailInput);
      passwordInput.removeEventListener('input', onPasswordInput);
      root.remove();
    },
  };
}
